import { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CefZScoreGauge — Phase 5 (Jun 2026)
 *
 * Visualises a CEF's current price-to-NAV premium/discount against
 * its own trailing 52-week distribution, expressed as a Z-score —
 * "how many standard deviations off the historical norm is this fund
 * trading right now?"
 *
 * Why Z-score matters for CEFs (from the user's product spec):
 *   A fund at a 5% discount LOOKS like a bargain, but if it
 *   historically trades at a 12% discount, you're actually overpaying.
 *   Z-score normalises the current premium/discount against the
 *   fund's own historical band, giving a "rich vs cheap RELATIVE TO
 *   THIS FUND" read.
 *
 * Z = (current − 52w_avg) / σ_52w
 *
 * Limitation: we have `current_discount` and `discount_52wk_avg` from
 * the curated CEF data, but NOT a daily-history-derived 52w σ. We
 * approximate σ from the Morningstar strategy category, which gives a
 * statistically honest read for the typical CEF in that class. The
 * tooltip on the gauge explicitly discloses this approximation.
 *
 * Bucket labels (per user spec):
 *   Z ≤ −2          → Extreme Bargain
 *   −2 < Z ≤ −1     → Buy Zone
 *   −1 < Z < +1     → Normal
 *   +1 ≤ Z < +2     → Expensive
 *   Z ≥ +2          → Severely Overpriced
 */

// Empirical typical 52-week σ of premium/discount, by Morningstar
// strategy class. Approximate, conservative. Used only when daily NAV
// history isn't available (which is always, today).
const STRATEGY_SIGMA_MAP = [
  { match: /municipal/i,                            sigma: 2.5,  category: 'Municipal' },
  { match: /senior loan|loan part/i,                sigma: 3.5,  category: 'Senior Loans' },
  { match: /high yield|high-income|high income/i,   sigma: 3.5,  category: 'High Yield Bond' },
  { match: /taxable bond|corporate bond|fixed inc/i,sigma: 3.0,  category: 'Taxable Bond' },
  { match: /mortgage|mbs/i,                         sigma: 4.0,  category: 'Mortgage' },
  { match: /preferred/i,                            sigma: 3.5,  category: 'Preferred' },
  { match: /convertible/i,                          sigma: 4.0,  category: 'Convertible' },
  { match: /real estate|reit/i,                     sigma: 6.0,  category: 'Real Estate' },
  { match: /mlp|midstream|energy infrastructure/i,  sigma: 6.5,  category: 'MLP / Energy' },
  { match: /utilities|infrastructure/i,             sigma: 4.5,  category: 'Utilities / Infra' },
  { match: /equity covered call|buy.write|option/i, sigma: 4.0,  category: 'Covered Call' },
  { match: /emerging|international|global/i,        sigma: 5.0,  category: 'International' },
  { match: /sector equity|technology|health|finan/i,sigma: 5.0,  category: 'Sector Equity' },
  { match: /equity/i,                               sigma: 4.5,  category: 'Equity' },
  { match: /multi.sector|multi sector|allocation/i, sigma: 4.0,  category: 'Multi-Sector' },
  { match: /multi.asset|balanced/i,                 sigma: 3.5,  category: 'Balanced' },
];

const DEFAULT_SIGMA = 4.0;

function estimateSigmaForStrategy(strategy) {
  if (!strategy || typeof strategy !== 'string') {
    return { sigma: DEFAULT_SIGMA, category: 'Default' };
  }
  for (const row of STRATEGY_SIGMA_MAP) {
    if (row.match.test(strategy)) return { sigma: row.sigma, category: row.category };
  }
  return { sigma: DEFAULT_SIGMA, category: 'Default' };
}

const Z_BUCKETS = [
  { max: -2.0, label: 'Extreme Bargain',      cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 ring-emerald-400/50',  tip: 'The current discount is much deeper than this fund\u2019s historical norm \u2014 a rare buying opportunity if the fundamentals haven\u2019t deteriorated.' },
  { max: -1.0, label: 'Buy Zone',             cls: 'bg-green-100 text-green-700 dark:bg-green-950/60 dark:text-green-300 ring-green-400/50',          tip: 'Trading meaningfully cheaper than its 52-week average premium/discount \u2014 historically a favourable entry window.' },
  { max:  1.0, label: 'Normal',               cls: 'bg-muted text-foreground ring-border/50',                                                          tip: 'Trading roughly in line with its 52-week historical premium/discount \u2014 nothing unusual to act on.' },
  { max:  2.0, label: 'Expensive',            cls: 'bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 ring-amber-400/50',          tip: 'More expensive than normal vs its 52-week historical band \u2014 the spread to NAV has narrowed (or premium widened) beyond typical levels.' },
  { max:  Infinity, label: 'Severely Overpriced', cls: 'bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300 ring-rose-400/50',           tip: 'Trading dramatically richer than its historical norm \u2014 mean-reversion risk is elevated.' },
];

function bucketForZ(z) {
  for (const b of Z_BUCKETS) {
    if (z <= b.max) return b;
  }
  return Z_BUCKETS[Z_BUCKETS.length - 1];
}

export default function CefZScoreGauge({ ticker, currentDiscount, avg52wkDiscount, strategy }) {
  // Phase 5 follow-up (Jun 2026) — opportunistic precision upgrade.
  // When the `cef-nav-history` cron has accumulated ≥20 daily
  // observations for this ticker, we pull the REAL trailing-year
  // mean + stddev and use those instead of the strategy-class σ
  // approximation. Falls back silently when stats aren't ready yet
  // (first ~20 trading days after a fund first appears in our CEF
  // universe).
  const [realStats, setRealStats] = useState(null);
  useEffect(() => {
    if (!ticker) return undefined;
    let cancelled = false;
    axios
      .get(`${API}/stocks/${encodeURIComponent(ticker)}/cef-nav-stats`, { withCredentials: true })
      .then((r) => { if (!cancelled) setRealStats(r.data || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [ticker]);

  // Inputs validation. Skip the whole gauge if we don't have both
  // signals \u2014 a Z-score against an unknown mean is meaningless.
  if (
    currentDiscount == null ||
    avg52wkDiscount == null ||
    !Number.isFinite(Number(currentDiscount)) ||
    !Number.isFinite(Number(avg52wkDiscount))
  ) {
    return null;
  }

  const cur = Number(currentDiscount);
  // Real stats win when they're ready (≥ 20 observations); otherwise
  // use the curated 52w average and a strategy-class σ approximation.
  const useReal = !!(realStats && realStats.ready && realStats.stdev_discount_pct > 0);
  const avg = useReal ? Number(realStats.mean_discount_pct) : Number(avg52wkDiscount);
  const { sigma: estSigma, category } = estimateSigmaForStrategy(strategy);
  const sigma = useReal ? Number(realStats.stdev_discount_pct) : estSigma;
  if (sigma <= 0) return null;

  const sigmaLabel = useReal
    ? `${realStats.n_observations}-day measured`
    : `${category}-class estimate`;

  // NAV history depth indicator — shown in both states. When real
  // stats are active it confirms how mature the measurement is; when
  // we're still on the estimate it tracks progress toward the 20-day
  // threshold so users can see when the upgrade will happen.
  const depthDays = realStats?.n_observations || 0;

  const z = (cur - avg) / sigma;
  // Clamp display position to the visible -3 \u2026 +3 scale.
  const zClamped = Math.max(-3, Math.min(3, z));
  // Map to 0\u2026100% across the gauge bar.
  const pct = ((zClamped + 3) / 6) * 100;

  const bucket = bucketForZ(z);
  const deltaPts = cur - avg;
  const direction = deltaPts < 0 ? 'wider' : 'narrower';

  // Gradient: emerald (cheap) -> neutral -> rose (expensive).
  const gradient = 'linear-gradient(to right, rgba(16, 185, 129, 0.45) 0%, rgba(132, 204, 22, 0.35) 25%, rgba(148, 163, 184, 0.30) 50%, rgba(245, 158, 11, 0.35) 75%, rgba(244, 63, 94, 0.45) 100%)';

  // Tooltip with all the math exposed so a curious user can verify.
  const mathTip = `Z = (current ${cur >= 0 ? '+' : ''}${cur.toFixed(2)}% − avg ${avg >= 0 ? '+' : ''}${avg.toFixed(2)}%) ÷ σ ${sigma.toFixed(2)}% (${sigmaLabel})\n→ Z = ${z.toFixed(2)}\n\n${useReal ? `σ is the actual measured stddev of this fund's daily premium/discount over the last ${realStats.n_observations} trading days, sourced from the cef-nav-history cron series.` : "σ is approximated from the typical 52-week premium/discount volatility for the fund's Morningstar strategy class because daily NAV history is not yet available. Treat the Z as a directional guide, not a precise statistic."}`;

  return (
    <div
      className="mt-2.5 pt-2 border-t border-teal-300/50 dark:border-teal-700/40"
      data-testid="cef-zscore-gauge"
    >
      {/* Header row: label + Z value + bucket pill */}
      <div className="flex items-center justify-between flex-wrap gap-1.5 mb-1.5">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-bold uppercase tracking-wide text-teal-800 dark:text-teal-300">
            1-Year Z-Score
          </span>
          <span
            className="text-xs font-mono font-bold text-foreground tabular-nums"
            data-testid="cef-zscore-value"
            title={mathTip}
          >
            {z >= 0 ? '+' : ''}{z.toFixed(2)}σ
          </span>
        </div>
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ring-1 cursor-help ${bucket.cls}`}
          title={bucket.tip}
          data-testid="cef-zscore-bucket"
        >
          {bucket.label}
        </span>
      </div>

      {/* Gauge bar — gradient track, tick marks at -2/-1/0/+1/+2,
          and a single dot showing where the current Z sits. */}
      <div className="relative h-6" data-testid="cef-zscore-bar">
        {/* Gradient track */}
        <div
          className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-2 rounded-full border border-border/40"
          style={{ background: gradient }}
        />
        {/* Tick marks at each integer σ */}
        {[-3, -2, -1, 0, 1, 2, 3].map((tick) => {
          const tp = ((tick + 3) / 6) * 100;
          const isZero = tick === 0;
          return (
            <div
              key={tick}
              className={`absolute top-1/2 -translate-y-1/2 ${isZero ? 'h-3 w-px bg-foreground/70' : 'h-2 w-px bg-border'}`}
              style={{ left: `${tp}%` }}
            />
          );
        })}
        {/* Current Z dot */}
        <div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3.5 h-3.5 rounded-full bg-foreground border-2 border-card shadow"
          style={{ left: `${pct}%` }}
          data-testid="cef-zscore-dot"
          title={mathTip}
        />
      </div>

      {/* Scale labels */}
      <div className="flex items-center justify-between text-[9px] font-mono text-muted-foreground mt-0.5">
        <span>−3σ</span>
        <span>−2σ</span>
        <span>−1σ</span>
        <span className="font-bold text-foreground">0</span>
        <span>+1σ</span>
        <span>+2σ</span>
        <span>+3σ</span>
      </div>

      {/* NAV history depth — sub-line showing exactly how mature the
          σ measurement is for this ticker. When using the strategy
          estimate, this also tracks progress toward the 20-day
          switchover threshold. */}
      <div className="flex items-center justify-between mt-1" data-testid="cef-zscore-depth">
        <span
          className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground cursor-help"
          title={
            depthDays === 0
              ? 'No NAV snapshots collected for this ticker yet. The daily cef-nav-history cron will start adding them tonight.'
              : useReal
                ? `${depthDays} daily NAV snapshots have been collected for this fund. σ is computed directly from this series.`
                : `${depthDays} of 20 daily NAV snapshots collected. Once 20 are accumulated (≈ ${Math.max(0, 20 - depthDays)} more trading day${20 - depthDays === 1 ? '' : 's'}), the gauge will switch from the strategy-class estimate to a precise per-fund σ.`
          }
        >
          NAV History Depth: <span className="text-foreground font-bold">{depthDays}</span> day{depthDays === 1 ? '' : 's'}
        </span>
        {!useReal && depthDays > 0 && depthDays < 20 && (
          <span
            className="text-[9px] font-mono uppercase tracking-wider text-amber-600 dark:text-amber-400"
            title={`${20 - depthDays} more trading day${20 - depthDays === 1 ? '' : 's'} until the gauge upgrades to measured σ.`}
          >
            {depthDays}/20 to precise
          </span>
        )}
        {useReal && (
          <span
            className="text-[9px] font-mono uppercase tracking-wider text-emerald-600 dark:text-emerald-400 cursor-help"
            title="σ is computed from this fund's own daily NAV history — no class-based approximation."
          >
            Precise σ <span aria-hidden className="opacity-70">ⓘ</span>
          </span>
        )}
      </div>

      {/* Plain-English footer */}
      <p className="text-[10px] text-muted-foreground italic leading-relaxed mt-1.5">
        Current spread is{' '}
        <span className="font-semibold text-foreground">
          {Math.abs(deltaPts).toFixed(1)} pts {direction}
        </span>{' '}
        than its {useReal ? `${realStats.n_observations}-day measured average` : '52-week average'}. σ {useReal
          ? `measured from ${realStats.n_observations} daily snapshots (precise).`
          : `estimated from ${category}-class CEFs (≈${sigma.toFixed(1)}%) because daily NAV history isn't available — treat as directional, not precise.`}
      </p>
    </div>
  );
}
